// TanStack Query hooks for the workspace module (members, invites, Principal profile, legal instruments,
// consents, activity, comments). Query keys: ['w', wsId, 'workspace', ...].
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AcceptInviteResponse,
  ActivityList,
  AttorneyDeclarationRequest,
  CommentDto,
  CommentList,
  ConsentRecordDto,
  ConsentsResponse,
  CreateInviteRequest,
  CreateInviteResponse,
  EntityType,
  InvitePreview,
  LegalInstrumentDto,
  MembersResponse,
  MentionableList,
  NhsNumberResponse,
  PrincipalConsentRequest,
  PrincipalProfile,
  SaveLegalInstrumentRequest,
  SignatureResponse,
  TransferOwnershipRequest,
  UpdateMemberRequest,
  UpdatePrincipalRequest,
  WithdrawConsentRequest,
} from '@seventide/types';
import { api, wsPath } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useWorkspace } from '../../hooks/useWorkspace';

const M = 'workspace';
export const wk = {
  all: (wsId: string) => qk.ws(wsId, M),
  members: (wsId: string) => qk.ws(wsId, M, 'members'),
  principal: (wsId: string) => qk.ws(wsId, M, 'principal'),
  instruments: (wsId: string) => qk.ws(wsId, M, 'legal-instruments'),
  consents: (wsId: string) => qk.ws(wsId, M, 'consents'),
  activity: (wsId: string, q?: unknown) => (q ? qk.ws(wsId, M, 'activity', q) : qk.ws(wsId, M, 'activity')),
  comments: (wsId: string, entityType?: string, entityId?: string) =>
    entityType ? qk.ws(wsId, M, 'comments', entityType, entityId) : qk.ws(wsId, M, 'comments'),
};

const path = (wsId: string, ...rest: string[]) => wsPath(wsId, M, ...rest);

/** After any change that affects permissions or the shell (roles, stage, nation, consent). */
function useInvalidate() {
  const qc = useQueryClient();
  const { wsId } = useWorkspace();
  return (opts: { detail?: boolean; me?: boolean } = {}) => {
    void qc.invalidateQueries({ queryKey: wk.all(wsId) });
    if (opts.detail) void qc.invalidateQueries({ queryKey: qk.workspace(wsId) });
    if (opts.me) void qc.invalidateQueries({ queryKey: qk.me });
  };
}

// ------------------------------------------------------------------ members & invites (FR-1.3, R-3.x)

export function useMembers() {
  const { wsId } = useWorkspace();
  return useQuery({ queryKey: wk.members(wsId), queryFn: () => api.get<MembersResponse>(path(wsId, 'members')) });
}

export function useCreateInvite() {
  const { wsId } = useWorkspace();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: CreateInviteRequest) => api.post<CreateInviteResponse>(path(wsId, 'invites'), body),
    onSuccess: () => invalidate(),
  });
}

export function useResendInvite() {
  const { wsId } = useWorkspace();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.post<CreateInviteResponse>(path(wsId, 'invites', id, 'resend')),
    onSuccess: () => invalidate(),
  });
}

export function useRevokeInvite() {
  const { wsId } = useWorkspace();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.delete(path(wsId, 'invites', id)),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateMember() {
  const { wsId } = useWorkspace();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateMemberRequest }) => api.patch(path(wsId, 'members', id), body),
    onSuccess: () => invalidate({ detail: true }),
  });
}

export function useRemoveMember() {
  const { wsId } = useWorkspace();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.delete(path(wsId, 'members', id)),
    onSuccess: () => invalidate({ detail: true, me: true }),
  });
}

export function useTransferOwnership() {
  const { wsId } = useWorkspace();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: TransferOwnershipRequest) => api.post(path(wsId, 'members', 'transfer-ownership'), body),
    onSuccess: () => invalidate({ detail: true, me: true }),
  });
}

// ------------------------------------------------------------------ Principal profile (FR-1.5)

export function usePrincipal() {
  const { wsId } = useWorkspace();
  return useQuery({ queryKey: wk.principal(wsId), queryFn: () => api.get<PrincipalProfile>(path(wsId, 'principal')) });
}

export function useUpdatePrincipal() {
  const { wsId } = useWorkspace();
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: UpdatePrincipalRequest) => api.patch<PrincipalProfile>(path(wsId, 'principal'), body),
    onSuccess: (data, body) => {
      qc.setQueryData(wk.principal(wsId), data);
      const shell = body.stage !== undefined || body.nation !== undefined || body.fullName !== undefined;
      invalidate({ detail: shell, me: shell });
    },
  });
}

/** The full NHS number, fetched only when someone asks to see it (audited server side). */
export function fetchNhsNumber(wsId: string) {
  return api.get<NhsNumberResponse>(path(wsId, 'principal', 'nhs-number'));
}

// ------------------------------------------------------------------ legal instruments (R-3.2)

export function useLegalInstruments() {
  const { wsId } = useWorkspace();
  return useQuery({
    queryKey: wk.instruments(wsId),
    queryFn: () => api.get<{ items: LegalInstrumentDto[] }>(path(wsId, 'legal-instruments')),
  });
}

export function useSaveInstrument() {
  const { wsId } = useWorkspace();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: SaveLegalInstrumentRequest }) =>
      id
        ? api.put<LegalInstrumentDto>(path(wsId, 'legal-instruments', id), body)
        : api.post<LegalInstrumentDto>(path(wsId, 'legal-instruments'), body),
    onSuccess: () => invalidate({ detail: true }),
  });
}

export function useDeleteInstrument() {
  const { wsId } = useWorkspace();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api.delete(path(wsId, 'legal-instruments', id)),
    onSuccess: () => invalidate({ detail: true }),
  });
}

// ------------------------------------------------------------------ consent (FR-1.4, R-5.2, R-5.3)

export function useConsents() {
  const { wsId } = useWorkspace();
  return useQuery({ queryKey: wk.consents(wsId), queryFn: () => api.get<ConsentsResponse>(path(wsId, 'consents')) });
}

export function useRecordPrincipalConsent() {
  const { wsId } = useWorkspace();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: PrincipalConsentRequest) => api.post<ConsentRecordDto>(path(wsId, 'consents', 'principal'), body),
    onSuccess: () => invalidate({ detail: true }),
  });
}

export function useAttorneyDeclaration() {
  const { wsId } = useWorkspace();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: AttorneyDeclarationRequest) => api.post<ConsentRecordDto>(path(wsId, 'consents', 'attorney'), body),
    onSuccess: () => invalidate({ detail: true }),
  });
}

export function useWithdrawConsent() {
  const { wsId } = useWorkspace();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: WithdrawConsentRequest }) =>
      api.post<ConsentRecordDto>(path(wsId, 'consents', id, 'withdraw'), body),
    onSuccess: () => invalidate({ detail: true }),
  });
}

export function useConsentSignature(id: string, enabled: boolean) {
  const { wsId } = useWorkspace();
  return useQuery({
    queryKey: qk.ws(wsId, M, 'consents', id, 'signature'),
    queryFn: () => api.get<SignatureResponse>(path(wsId, 'consents', id, 'signature')),
    enabled,
    staleTime: Infinity,
  });
}

// ------------------------------------------------------------------ activity (FR-1.6)

export interface ActivityFilters {
  module?: string;
  userId?: string;
  limit: number;
  offset: number;
}

export function useActivity(filters: ActivityFilters, opts: { enabled?: boolean } = {}) {
  const { wsId } = useWorkspace();
  return useQuery({
    queryKey: wk.activity(wsId, filters),
    queryFn: () =>
      api.get<ActivityList>(path(wsId, 'activity'), {
        query: { module: filters.module, userId: filters.userId, limit: filters.limit, offset: filters.offset },
      }),
    placeholderData: keepPreviousData,
    enabled: opts.enabled ?? true,
  });
}

// ------------------------------------------------------------------ comments (FR-1.7)

export function useComments(entityType: EntityType, entityId: string) {
  const { wsId } = useWorkspace();
  return useQuery({
    queryKey: wk.comments(wsId, entityType, entityId),
    queryFn: () => api.get<CommentList>(path(wsId, 'comments'), { query: { entityType, entityId } }),
    enabled: Boolean(entityId),
  });
}

export function useMentionable(entityType: EntityType, entityId: string, enabled: boolean) {
  const { wsId } = useWorkspace();
  return useQuery({
    // Per record: who can see a document depends on the document (SR-27).
    queryKey: qk.ws(wsId, M, 'mentionable', entityType, entityId),
    queryFn: () =>
      api.get<MentionableList>(path(wsId, 'comments', 'mentionable'), { query: { entityType, entityId } }),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Comment counts for a list of records (e.g. to show "3 comments" on list rows). */
export function useCommentCounts(entityType: EntityType, ids: string[]) {
  const { wsId } = useWorkspace();
  const sorted = [...ids].sort();
  return useQuery({
    queryKey: qk.ws(wsId, M, 'comment-counts', entityType, sorted),
    queryFn: () =>
      api.get<{ counts: Record<string, number> }>(path(wsId, 'comments', 'counts'), {
        query: { entityType, ids: sorted.join(',') },
      }),
    enabled: sorted.length > 0,
  });
}

/** `link` is the in-app path of the record (used in mention notifications). */
export function useCommentMutations(entityType: EntityType, entityId: string, link?: string) {
  const { wsId } = useWorkspace();
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: wk.comments(wsId, entityType, entityId) });
    void qc.invalidateQueries({ queryKey: qk.ws(wsId, M, 'comment-counts', entityType) });
    void qc.invalidateQueries({ queryKey: wk.activity(wsId) });
  };
  return {
    create: useMutation({
      mutationFn: (body: string) =>
        api.post<CommentDto>(path(wsId, 'comments'), { entityType, entityId, body, link }),
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: string }) =>
        api.patch<CommentDto>(path(wsId, 'comments', id), { body, link }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete(path(wsId, 'comments', id)),
      onSuccess: refresh,
    }),
  };
}

// ------------------------------------------------------------------ public invite (no workspace context)

export const invitePath = (workspaceId: string, token: string) =>
  `/api/invites/${encodeURIComponent(workspaceId)}/${encodeURIComponent(token)}`;

export function useInvitePreview(workspaceId: string, token: string, signedIn: boolean | undefined) {
  return useQuery({
    queryKey: ['invite', workspaceId, token, signedIn ?? null],
    queryFn: () => api.get<InvitePreview>(invitePath(workspaceId, token), { redirectOn401: false }),
    enabled: signedIn !== undefined,
    retry: false,
  });
}

export function acceptInvite(workspaceId: string, token: string, name?: string) {
  return api.post<AcceptInviteResponse>(`${invitePath(workspaceId, token)}/accept`, name ? { name } : {});
}
